import { useEffect, useState } from 'react';
import { useSearchParams, useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Upload, CreditCard, Banknote, Smartphone, Copy, Check, Home, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiFetchJson } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { trackPageView } from '../lib/analytics';

interface OrderStatusResponse {
  orderNum: string;
  status: string;
  finalPrice?: number;
  isConfirmed: boolean;
  createdAt: number;
  confirmedAt?: number;
}

interface SiteSettings {
  fps_id?: string;
  company_name?: string;
  business_phone?: string;
}

export default function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const params = useParams<{ orderNum?: string }>();
  const orderNum = params.orderNum || searchParams.get('orderNum');
  const totalFromQuery = searchParams.get('total');

  const [order, setOrder] = useState<OrderStatusResponse | null>(null);
  const [settings, setSettings] = useState<SiteSettings>({});
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const queryTotal = totalFromQuery ? parseInt(totalFromQuery, 10) : 0;
  const displayTotal = queryTotal || order?.finalPrice || 0;

  useEffect(() => {
    trackPageView('/order/success');
    apiFetchJson<SiteSettings>('/public/settings')
      .then((res) => {
        if (res.success && res.data) {
          setSettings(res.data);
        }
      })
      .catch(() => {});
    if (!orderNum) {
      setLoading(false);
      return;
    }
    apiFetchJson<OrderStatusResponse>(`/public/orders/${orderNum}/status`)
      .then((res) => {
        if (res.success && res.data) {
          setOrder(res.data);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [orderNum]);

  const handleCopy = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!orderNum) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="text-center py-20">找不到訂單資訊</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="py-12 md:py-20">
        <div className="max-w-xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={48} className="text-green-600" />
            </div>
            <h1 className="text-3xl font-bold mb-2">訂單已確認</h1>
            <p className="text-stone-600">感謝你的訂單，請按照以下方式付款。</p>
          </div>

          {loading ? (
            <div className="text-center py-8">載入中...</div>
          ) : (
            <div className="space-y-6">
              <div className="bg-stone-50 rounded-2xl p-6">
                <div className="flex justify-between items-center py-3 border-b border-stone-200">
                  <span className="text-stone-600">訂單編號</span>
                  <span className="font-bold text-xl">#{orderNum}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-stone-200">
                  <span className="text-stone-600">總金額</span>
                  <span className="font-bold text-2xl text-x1493-primary">
                    {displayTotal ? formatPrice(displayTotal) : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-stone-600">狀態</span>
                  <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-bold">
                    待付款
                  </span>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-amber-900 mb-4 flex items-center gap-2">
                  <CreditCard size={22} />
                  付款資訊
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Banknote size={20} className="text-amber-700 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-bold text-stone-800">FPS 轉數快 / PayMe</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-mono font-bold">{settings.fps_id || '—'}</p>
                        <button
                          onClick={() => settings.fps_id && handleCopy(settings.fps_id, 'fps')}
                          className="p-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 transition"
                        >
                          {copiedField === 'fps' ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Smartphone size={20} className="text-amber-700 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-stone-800">戶口名稱</p>
                      <p>{settings.company_name || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center gap-2">
                  <Upload size={22} />
                  上傳付款憑證
                </h3>
                <p className="text-stone-600 mb-4 text-sm">付款後請上傳憑證，我們會盡快確認。</p>
                <button
                  onClick={() => navigate(`/payment-proof/${orderNum}`)}
                  className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition flex items-center justify-center gap-2"
                >
                  <Upload size={20} />
                  上傳付款記錄
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/"
                  className="flex-1 py-3 rounded-xl font-bold border-2 border-stone-200 text-stone-700 hover:bg-stone-100 transition flex items-center justify-center gap-2"
                >
                  <Home size={18} />
                  返回首頁
                </Link>
                <button
                  onClick={() => navigate(`/order/${orderNum}`)}
                  className="flex-1 py-3 rounded-xl font-bold border-2 border-stone-200 text-stone-700 hover:bg-stone-100 transition flex items-center justify-center gap-2"
                >
                  <Search size={18} />
                  查看訂單
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
